/**
 * DashboardStore (Plan 01-13, Task 1; v1.1 wall-tablet opt-in).
 *
 * Zustand slice backing the dashboard dual-mode contract (DASH-01, DASH-02,
 * DASH-03, DASH-06).  Both `<IdleMode />` and `<InteractiveMode />` mount
 * simultaneously on `/`; `isIdle` toggles their visibility via opacity so
 * the clock and timer state survive every mode transition.
 *
 * v1.1 change — wall-tablet mode is now OPT-IN.  By default the app opens
 * straight into the interactive ("welcome") surface for whoever is logged
 * in; the ambient idle hub is the kitchen-tablet display you switch a
 * specific device into.  The choice is persisted per-device in
 * `localStorage` under `stell.wallMode` so a configured tablet boots back
 * into the idle hub across reloads, while phones never auto-idle.
 *
 * State:
 *   - `wallMode` — true = this device is a kitchen wall tablet.  Read from
 *     localStorage at store creation; drives both the cold-boot `isIdle`
 *     seed and whether the 60s idle timer runs at all.
 *   - `isIdle` — true = idle view visible.  Seeds to `wallMode`: a wall
 *     tablet arrives at the ambient surface on cold boot; every other
 *     device opens interactive.
 *   - `deviceClass` — drives the NavShell variant (tablet = left rail,
 *     phone = bottom tabs).  Populated by `<InteractiveMode />` on mount via
 *     `detectDeviceClass()`.  Desktop default covers SSR + unit tests.
 *   - `idleHolds` / `idleHeldSince` — the idle-hold register (issue #29).  See
 *     the `holdIdle()` docblock below.
 *
 * Actions:
 *   - `wake()` flips `isIdle` false
 *   - `goIdle()` flips `isIdle` true — the 60s timer calls this
 *   - `enableWallMode()` / `disableWallMode()` persist the per-device choice
 *     and snap `isIdle` to match (on → idle hub, off → interactive)
 *   - `setDeviceClass(d)` updates the device class once
 *   - `holdIdle()` takes one reference on the idle-hold register and returns
 *     the matching release closure
 *
 * Selectors (`useIsIdle`, `useDeviceClass`, `useWallMode`) exist so
 * downstream components can subscribe to exactly the slice they need
 * without re-rendering on unrelated changes.
 *
 * `resetDashboardStore()` (bottom of this file) is the single reset helper
 * every test suite uses — see its docblock before adding a field here.
 */
import { create } from 'zustand';

export type DeviceClass = 'tablet' | 'phone' | 'desktop';

const WALL_MODE_KEY = 'stell.wallMode';

function readWallMode(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(WALL_MODE_KEY) === '1';
  } catch {
    // Private-mode / disabled storage — treat as a normal (non-wall) device.
    return false;
  }
}

function persistWallMode(on: boolean): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (on) localStorage.setItem(WALL_MODE_KEY, '1');
    else localStorage.removeItem(WALL_MODE_KEY);
  } catch {
    // Quota / private-mode write failure is non-fatal — the in-memory store
    // still reflects the choice for this session.
  }
}

export interface DashboardState {
  isIdle: boolean;
  wallMode: boolean;
  deviceClass: DeviceClass;
  /**
   * Number of live idle holds (issue #29).  Zero = the 60s timer may fade the
   * screen; non-zero = something on screen is still worth looking at (a
   * streaming assistant reply, an open BottomSheet) and the timer must defer.
   *
   * A counter, not a boolean: two independent holders (a sheet open *over* a
   * streaming reply) would otherwise cancel each other out when the first one
   * released.
   */
  idleHolds: number;
  /**
   * Epoch ms of the 0→1 edge, or null while `idleHolds === 0`.  Read only by
   * the deadman ceiling in `useIdleTimer` — a hold that never releases must
   * not keep an ambient wall tablet lit forever.
   */
  idleHeldSince: number | null;
  wake: () => void;
  goIdle: () => void;
  enableWallMode: () => void;
  disableWallMode: () => void;
  setDeviceClass: (d: DeviceClass) => void;
  holdIdle: () => () => void;
}

export const useDashboard = create<DashboardState>((set) => {
  const wallMode = readWallMode();
  return {
    // Seed idle from the persisted per-device choice: a configured wall
    // tablet cold-boots into the ambient hub; everything else opens
    // interactive so the logged-in user lands on their welcome surface.
    isIdle: wallMode,
    wallMode,
    deviceClass: 'desktop',
    idleHolds: 0,
    idleHeldSince: null,
    wake: () => set({ isIdle: false }),
    // Idempotent on purpose: returning the SAME state object makes zustand's
    // `Object.is` check skip the subscriber notification entirely.  The idle
    // timer re-arms perpetually (see `useIdleTimer`'s fire path), so a wall
    // tablet parked in the ambient hub calls this every 60s forever — a fresh
    // `{ isIdle: true }` literal would never compare equal and would wake
    // every mounted selector ~1440 times a day for no state change.
    goIdle: () => set((s) => (s.isIdle ? s : { isIdle: true })),
    enableWallMode: () => {
      persistWallMode(true);
      set({ wallMode: true, isIdle: true });
    },
    disableWallMode: () => {
      persistWallMode(false);
      set({ wallMode: false, isIdle: false });
    },
    setDeviceClass: (d) => set({ deviceClass: d }),
    /**
     * Take one reference on the idle-hold register; returns its release.
     *
     * The store stays a dumb counter on purpose — it knows nothing about
     * *why* a caller holds (streaming, sheet open, grace period).  That
     * policy lives in the feature that owns the reason.
     *
     * The returned closure is SINGLE-USE.  A double release would decrement
     * a reference this caller no longer owns and could drop the count below
     * another holder's contribution, so `released` latches the first call.
     * The `Math.max(0, …)` floor guarantees only that the count never goes
     * negative — nothing more.  In particular it does NOT make a release
     * closure outstanding across an external reset (a test, or HMR, forcing
     * `idleHolds` back to 0) harmless: if a new holder has acquired since the
     * reset, that stale release still decrements the live holder's reference.
     * The defence against that is not reusing a release across a reset.
     *
     * Never call this directly from a component — use `useIdleHold()`, which
     * ties the release to effect cleanup so an unmount cannot leak a hold.
     */
    holdIdle: () => {
      let released = false;
      set((s) => ({
        idleHolds: s.idleHolds + 1,
        // Stamp only on the 0→1 edge: the ceiling measures how long the
        // screen has been held *continuously*, not since the last holder
        // joined.  Re-stamping would let a stream of overlapping holds walk
        // the deadman forward indefinitely.
        idleHeldSince: s.idleHolds === 0 ? Date.now() : s.idleHeldSince,
      }));
      return () => {
        if (released) return;
        released = true;
        set((s) => {
          const next = Math.max(0, s.idleHolds - 1);
          return {
            idleHolds: next,
            // Clear only on the 1→0 edge — while other holders remain, the
            // original stamp is still the continuous-hold baseline.
            idleHeldSince: next === 0 ? null : s.idleHeldSince,
          };
        });
      };
    },
  };
});

// Narrow selectors for hot-path consumers — IdleMode's opacity transition
// re-reads `isIdle` every frame, so we want a minimal subscription.
export const useIsIdle = (): boolean => useDashboard((s) => s.isIdle);
export const useWallMode = (): boolean => useDashboard((s) => s.wallMode);
export const useDeviceClass = (): DeviceClass =>
  useDashboard((s) => s.deviceClass);

/**
 * Reset the store to a known baseline — the single reset used by every test
 * suite that touches the dashboard.
 *
 * It exists because `setState` MERGES SHALLOWLY.  A key a suite forgets to
 * name is not reset to anything; it keeps whatever the previous test left in
 * this module-wide store.  That is how a leaked `idleHolds` from one test
 * silently suppresses the next test's 60s idle fire — a failure that reads as
 * "the timer is broken" rather than "the reset was incomplete".  Nine reset
 * sites across eight files had to be edited by hand when `idleHolds` /
 * `idleHeldSince` were added; this helper is so that never happens again.
 *
 * A NEW `DashboardState` FIELD BELONGS IN THE DEFAULTS HERE, not in N test
 * files.
 *
 * Not derived from `getInitialState()` on purpose: the store seeds `wallMode`
 * and `isIdle` from `localStorage` at module load, so its initial state is
 * whatever the environment happened to hold — not a fixed default a test can
 * rely on.  The baseline below is the explicit non-wall device.
 *
 * `overrides` covers the suites that need a different starting point (an idle
 * wall tablet, a phone device class); they still get every other key reset.
 */
export function resetDashboardStore(
  overrides: Partial<DashboardState> = {},
): void {
  useDashboard.setState({
    isIdle: false,
    wallMode: false,
    deviceClass: 'desktop',
    idleHolds: 0,
    idleHeldSince: null,
    ...overrides,
  });
}
